// Demonstrativo de remuneração (E8): o que cada pessoa ganhou num período,
// mês a mês. Serve de comprovante de renda e de resumo no fim do ano.
//
//   acertado:   os créditos e descontos dos acertos fechados e do histórico
//               dos programas antigos (pela data de cada um: a da carga na
//               comissão e no agenciamento, o último dia pago nas diárias, o
//               mês no fixo)
//   pela folha: o fixo mensal pago fora do acerto, pelo cadastro
//   a acertar:  o que foi lançado e ainda não entrou num acerto, e o que as
//               regras dão para as cargas e viagens ainda não acertadas
//   recebido:   vales e pagamentos dos acertos, menos as devoluções; e, à
//               parte, o que disso foi reembolso das despesas que a pessoa
//               pagou (diesel, pedágio…), que já estão nas despesas
//
// O saldo de implantação não é ganho do período (é o que estava em aberto
// nos programas antigos) e fica de fora.
import { mesesEntre, periodoDoMes } from './custos.js';

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => c / 100;
const vivo = (x) => x && !x.deleted_at;

export const ORDEM_CREDITOS = ['comissao', 'valor_por_carga', 'valor_por_viagem', 'diaria', 'repasse_estadia', 'fixo_mensal', 'agenciamento', 'bonus', 'outro'];
export const ORDEM_DESCONTOS = ['avaria', 'multa', 'outro'];

function somar(mapa, chave, c) {
  mapa[chave] = (mapa[chave] ?? 0) + c;
}

function emReais(mapa) {
  return Object.fromEntries(Object.entries(mapa).filter(([, c]) => c !== 0).map(([k, c]) => [k, reais(c)]));
}

const somaDe = (mapa) => Object.values(mapa).reduce((s, c) => s + c, 0);

// `tabelas`: { viagens, fretes, lancamentos, regras } da empresa.
// `contas`: { regraValendo, creditosDaViagem, agenciamentosPendentes } de lib/acerto.js.
export function ganhosDaPessoa(colaboradorId, tabelas, { de, ate, hoje }, contas) {
  const lancamentos = (tabelas.lancamentos ?? []).filter((l) => vivo(l) && l.colaborador_id === colaboradorId);
  const viagens = (tabelas.viagens ?? []).filter((v) => vivo(v) && v.status !== 'cancelada');
  const fretes = (tabelas.fretes ?? []).filter(vivo);
  const regras = (tabelas.regras ?? []).filter(vivo);
  const todosCreditos = (tabelas.lancamentos ?? []).filter((l) => vivo(l) && l.tipo === 'credito');
  const meses = mesesEntre(de, ate);
  const inicio = `${meses[0]}-01`;
  const fim = periodoDoMes(meses.at(-1)).ate;
  const noPeriodo = (dia) => dia >= inicio && dia <= fim;

  const porMes = new Map(meses.map((m) => [m, { creditos: {}, descontos: {}, folha: 0 }]));
  const aAcertar = [];
  const recebido = { vales: 0, pagamentos: 0, devolucoes: 0, despesas: 0 };
  const implantacao = [];
  // Parte do período veio dos programas antigos (eles não registravam os
  // pagamentos das comissões: no recebido ficam só os vales).
  let historico = false;

  for (const l of lancamentos) {
    if (!noPeriodo(l.data)) continue;
    if (l.origem === 'importacao') historico = true;
    const c = centavos(l.valor);
    if (l.tipo === 'vale') recebido.vales += c;
    else if (l.tipo === 'pagamento') recebido.pagamentos += c;
    else if (l.tipo === 'devolucao') recebido.devolucoes += c;
    else if (l.tipo === 'despesa' && l.pago_por === 'colaborador') recebido.despesas += c;
    if (l.tipo !== 'credito' && l.tipo !== 'desconto') continue;
    if (l.origem === 'implantacao') {
      implantacao.push({ data: l.data, valor: (l.tipo === 'desconto' ? -1 : 1) * reais(c) });
      continue;
    }
    if (l.acerto_id || l.origem === 'importacao') {
      const m = porMes.get(l.data.slice(0, 7));
      if (l.tipo === 'credito') somar(m.creditos, l.subtipo ?? 'outro', c);
      else somar(m.descontos, l.subtipo ?? 'outro', c);
    } else {
      aAcertar.push({ data: l.data, subtipo: l.subtipo ?? 'outro', tipo: l.tipo, valor: (l.tipo === 'desconto' ? -1 : 1) * reais(c), descricao: l.descricao ?? null, lancado: true });
    }
  }

  // Pelas regras, o que ainda não foi acertado (até o fim do período ou hoje)
  const ateHoje = fim < hoje ? fim : hoje;
  for (const v of viagens) {
    if (v.motorista_id !== colaboradorId || v.importacao_id || v.status === 'acertada' || v.data_saida > ateHoje) continue;
    const cargas = fretes.filter((f) => f.viagem_id === v.id && !f.acerto_id && f.data_carregamento <= ateHoje);
    for (const linha of contas.creditosDaViagem(v, cargas, regras, { jaLancados: todosCreditos, ate: ateHoje })) {
      if (noPeriodo(linha.data)) aAcertar.push({ data: linha.data, subtipo: linha.subtipo, tipo: 'credito', valor: linha.valor, descricao: linha.descricao, lancado: false });
    }
  }
  for (const linha of contas.agenciamentosPendentes(fretes, colaboradorId, todosCreditos, ateHoje)) {
    if (noPeriodo(linha.data)) aAcertar.push({ data: linha.data, subtipo: 'agenciamento', tipo: 'credito', valor: linha.valor, descricao: linha.descricao, lancado: false });
  }

  // Fixo mensal: o da folha entra no mês (pelo cadastro); o do acerto, se
  // ainda não foi lançado, fica para acertar.
  for (const mes of meses) {
    const p = periodoDoMes(mes);
    const dia = p.ate < hoje ? p.ate : hoje;
    if (dia < p.de) continue;
    const regra = contas.regraValendo(regras, 'fixo_mensal', { colaboradorId, dia });
    if (!regra || !(Number(regra.valor) > 0)) continue;
    if (regra.fixo_pagamento === 'folha') {
      porMes.get(mes).folha += centavos(regra.valor);
    } else if (!todosCreditos.some((l) => l.colaborador_id === colaboradorId && l.subtipo === 'fixo_mensal' && l.data.slice(0, 7) === mes)) {
      aAcertar.push({ data: p.de, subtipo: 'fixo_mensal', tipo: 'credito', valor: Number(regra.valor), descricao: `Fixo de ${mes}`, lancado: false });
    }
  }

  const linhasDoMes = meses.map((mes) => {
    const m = porMes.get(mes);
    const creditos = somaDe(m.creditos) + m.folha;
    const descontos = somaDe(m.descontos);
    return {
      mes,
      creditos: emReais(m.creditos),
      descontos: emReais(m.descontos),
      folha: reais(m.folha),
      totalCreditos: reais(creditos),
      totalDescontos: reais(descontos),
      ganho: reais(creditos - descontos),
    };
  });
  const totais = { creditos: {}, descontos: {}, folha: 0 };
  for (const m of porMes.values()) {
    for (const [k, c] of Object.entries(m.creditos)) somar(totais.creditos, k, c);
    for (const [k, c] of Object.entries(m.descontos)) somar(totais.descontos, k, c);
    totais.folha += m.folha;
  }
  const totalCreditos = somaDe(totais.creditos) + totais.folha;
  const totalDescontos = somaDe(totais.descontos);
  aAcertar.sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : 0));
  return {
    de: inicio,
    ate: fim,
    meses: linhasDoMes,
    totais: {
      creditos: emReais(totais.creditos),
      descontos: emReais(totais.descontos),
      folha: reais(totais.folha),
      totalCreditos: reais(totalCreditos),
      totalDescontos: reais(totalDescontos),
      ganho: reais(totalCreditos - totalDescontos),
    },
    aAcertar: { itens: aAcertar, total: reais(aAcertar.reduce((s, x) => s + centavos(x.valor), 0)) },
    recebido: {
      vales: reais(recebido.vales),
      pagamentos: reais(recebido.pagamentos),
      devolucoes: reais(recebido.devolucoes),
      total: reais(recebido.vales + recebido.pagamentos - recebido.devolucoes),
      // Despesas que ela pagou no período (o acerto devolve; não é remuneração)
      despesas: reais(recebido.despesas),
      liquido: reais(recebido.vales + recebido.pagamentos - recebido.devolucoes - recebido.despesas),
    },
    implantacao,
    historico,
  };
}

// Tem alguma coisa para mostrar no período?
export function temGanhos(g) {
  return g.totais.totalCreditos !== 0 || g.totais.totalDescontos !== 0 || g.aAcertar.itens.length > 0 || g.recebido.total !== 0;
}
